import invariant from 'tiny-invariant'
import { objectKeys } from 'ts-extras'

import { ServiceFeeTableHeading } from '@/components/misc/ServiceFeesTable/ServiceFeesTableHeaders'
import { ServiceFeeDetail } from '@/components/misc/ServiceFeesTable/ServiceFeesTableRows'
import {
  API_PATH_INTERNAL,
  COMMERCIAL_AGREEMENT_TYPE,
  REFERRAL_CONSENT_LETTER_VARIANT,
  REFERRAL_CONSENT_STAGE,
  SERVICE_FEE_CATEGORY_TYPES,
  SERVICE_FEE_DURATION_OPTIONS,
  SERVICE_FEE_SHARE_TYPE,
} from '@/lib/constants'
import { getOrFail } from '@/lib/helpers/helperFunctions'
import apiService from '@/lib/services/apiService'
import serviceAreaService from '@/lib/services/serviceAreaService'
import {
  CommercialAgreementTypeOption,
  ServiceFeeInput,
} from '@/redux/slices/commercialAgreementsForm'
import { ServiceFeeEntity } from '@/types/entities'
import {
  ReferralConsentLetterVariant,
  ReferralConsentStage,
  SelectOption,
} from '@/types/misc'
import { ValidateServiceFeeRequest } from '@/types/requests/service-fees'
import { CommercialAgreementMetadataCompanyService } from '@/types/responses/commercial-agreements'
import { ServiceAreaItem } from '@/types/responses/common-data'
import { ValidateServiceFeeResponse } from '@/types/responses/service-fees'

export const SERVICE_AREA_OPTION_ALL = 'All'
export const SERVICE_AREA_OPTION_OTHER = 'OTHER'

class ServiceFeeService {
  private endpoint = `${API_PATH_INTERNAL}/service-fees`

  async validateServiceFee(
    request: ValidateServiceFeeRequest,
  ): Promise<ValidateServiceFeeResponse> {
    const response = await apiService.post(`${this.endpoint}/validate`, request)

    return response.data
  }

  getServiceAreaOptions(
    serviceAreas: CommercialAgreementMetadataCompanyService[],
  ): SelectOption[] {
    const options = serviceAreas.map((serviceArea) => ({
      label: serviceArea.name,
      value: serviceArea['@id'],
    }))

    return [
      {
        label: this.getAllServiceAreasLabel(),
        value: SERVICE_AREA_OPTION_ALL,
      },
      ...options,
      {
        label: 'Other (please specify)',
        value: SERVICE_AREA_OPTION_OTHER,
      },
    ]
  }

  getAllServiceAreasLabel(): string {
    return 'All services (unless otherwise specified)'
  }

  getServiceFeeCategoryOptions(): SelectOption[] {
    return objectKeys(SERVICE_FEE_CATEGORY_TYPES).map((key) => ({
      label: SERVICE_FEE_CATEGORY_TYPES[key].label,
      value: SERVICE_FEE_CATEGORY_TYPES[key].value,
    }))
  }

  getServiceFeeCategoryByValue(value: string): SelectOption {
    const feeCategory = Object.entries(SERVICE_FEE_CATEGORY_TYPES).find(
      ([_, feeCategoryType]) => {
        return feeCategoryType.value === value
      },
    )

    return getOrFail(
      feeCategory?.[1],
      `Fee category not found for value "${value}"`,
    )
  }

  getShareTypeOptions(): SelectOption[] {
    return objectKeys(SERVICE_FEE_SHARE_TYPE).map((key) => ({
      label: SERVICE_FEE_SHARE_TYPE[key].label,
      value: SERVICE_FEE_SHARE_TYPE[key].value,
    }))
  }

  getDurationOptions(): SelectOption[] {
    return SERVICE_FEE_DURATION_OPTIONS
  }

  isAllServiceAreas(serviceFee: ServiceFeeDetail): boolean {
    // serviceArea can be 'All' in AddServiceFeeModal.tsx
    if (serviceFee.serviceArea === SERVICE_AREA_OPTION_ALL) {
      return true
    }

    // serviceAreaOther can be 'All' for already saved service fees
    if (
      !serviceFee.serviceArea &&
      serviceFee.serviceAreaOther === SERVICE_AREA_OPTION_ALL
    ) {
      return true
    }

    return false
  }

  normalizeServiceFee<
    InputType extends ServiceFeeInput | ValidateServiceFeeRequest,
  >(serviceFee: InputType): InputType {
    let normalizedServiceFee = { ...serviceFee }
    const serviceArea = serviceFee.serviceArea
    const feeCategory = serviceFee.feeCategory || ''

    // Clear the `serviceArea` field if the `All` option was selected and
    // set `serviceAreaOther` to 'All'
    if (serviceArea === SERVICE_AREA_OPTION_ALL) {
      normalizedServiceFee = {
        ...normalizedServiceFee,
        serviceArea: undefined,
        serviceAreaOther: SERVICE_AREA_OPTION_ALL,
      }
    }

    // Clear the `serviceArea` field if the `Other (please specify)` option
    // was selected
    if (serviceArea === SERVICE_AREA_OPTION_OTHER) {
      normalizedServiceFee = {
        ...normalizedServiceFee,
        serviceArea: undefined,
      }
    }

    if ('duration' in normalizedServiceFee) {
      normalizedServiceFee.duration = Number(normalizedServiceFee.duration)
    }

    if (feeCategory.trim() === '') {
      normalizedServiceFee.feeCategory = undefined
    }

    return normalizedServiceFee
  }

  getServiceAreaName(
    serviceFee: ServiceFeeDetail,
    serviceAreas: ServiceAreaItem[],
  ): string {
    const { serviceArea } = serviceFee

    if (this.isAllServiceAreas(serviceFee)) {
      return this.getAllServiceAreasLabel()
    }

    if (serviceFee.serviceAreaOther) {
      return serviceFee.serviceAreaOther
    }

    if (serviceArea) {
      return serviceAreaService.getServiceAreaByIri(serviceArea, serviceAreas)
        .name
    }

    return ''
  }

  isOtherFeeCategory(feeCategory: string) {
    return feeCategory === SERVICE_FEE_CATEGORY_TYPES.other.value
  }

  isOngoingFeesAgreement(feeCategory?: string) {
    return feeCategory === SERVICE_FEE_CATEGORY_TYPES.ongoingFees.value
  }

  shouldShowFeePassBackColumn(options: {
    serviceFees: ServiceFeeDetail[]
    isRecommendedPartnerTerms?: boolean
  }) {
    const { serviceFees, isRecommendedPartnerTerms } = options

    if (isRecommendedPartnerTerms) {
      return false
    }

    return serviceFees.some((serviceFee) => serviceFee.passBackFee)
  }

  shouldShowNotesColumn(serviceFees: ServiceFeeDetail[]) {
    return (
      serviceFees.length === 0 || // Show if table is empty
      serviceFees.some((serviceFee) => serviceFee.notes)
    )
  }

  shouldShowShareTypeColumn({
    isCompanyUser,
    isPreviewLetter,
  }: {
    isCompanyUser: boolean
    isPreviewLetter: boolean
  }) {
    return isCompanyUser && !isPreviewLetter
  }

  // This is a temporary check to ensure that the API handles migrating all
  // commercial agreements to the service fee structure.
  // TODO: Remove this method once the new service fee structure has been in
  // production for a few weeks.
  validateServiceFeesOrFail(serviceFees: ServiceFeeDetail[]) {
    if (!Array.isArray(serviceFees)) {
      throw new Error(
        `Expected serviceFees to be an array, but got ${serviceFees}. ` +
          "This is probably because the commercial terms haven't been migrated to " +
          'the new more granular service fees data format.',
      )
    }

    serviceFees.forEach((serviceFee) => {
      invariant(
        serviceFee.serviceArea || serviceFee.serviceAreaOther,
        'Expected serviceFee to have a serviceArea or serviceAreaOther',
      )
    })
  }
  shouldShowExampleColumn(options: {
    agreementType: CommercialAgreementTypeOption
    serviceFees: ServiceFeeDetail[]
    referralConsentStage: ReferralConsentStage
    consentLetterVariant: ReferralConsentLetterVariant
  }): boolean {
    const {
      serviceFees,
      agreementType,
      referralConsentStage,
      consentLetterVariant,
    } = options

    if (agreementType !== COMMERCIAL_AGREEMENT_TYPE.YFS) {
      return false
    }

    return (
      serviceFees.length === 0 || // Show if table is empty
      serviceFees.some((serviceFee) => {
        const example = serviceFeeService.getExample({
          serviceFee,
          consentLetterVariant,
          referralConsentStage,
        })

        return !!example
      })
    )
  }

  getTableHeaders(options: {
    serviceFees: ServiceFeeDetail[]
    showExampleColumn: boolean
    showActionsColumn: boolean
    agreementType: CommercialAgreementTypeOption
    isRecommendedPartnerTerms?: boolean
    isCompanyUser: boolean
    isPreviewLetter: boolean
  }): ServiceFeeTableHeading[] {
    const {
      serviceFees,
      showExampleColumn,
      showActionsColumn,
      isRecommendedPartnerTerms,
      isCompanyUser,
      isPreviewLetter,
    } = options

    const tableHeaders: ServiceFeeTableHeading[] = [
      {
        label: 'Service',
        className: 'sticky-column-start',
        style: { width: '130px' },
      },
      {
        label: 'Fee category',
        style: { width: '120px' },
      },
    ]

    if (
      serviceFeeService.shouldShowShareTypeColumn({
        isCompanyUser,
        isPreviewLetter,
      })
    ) {
      tableHeaders.push({
        label: 'Share type',
        style: { width: '120px' },
      })
    }

    tableHeaders.push({
      label: 'Share value',
      style: { width: '120px' },
    })

    if (
      serviceFeeService.shouldShowFeePassBackColumn({
        serviceFees,
        isRecommendedPartnerTerms,
      })
    ) {
      tableHeaders.push({
        label: 'Pass back some or all to your client?',
        style: { width: '150px' },
      })
    }

    if (serviceFeeService.shouldShowNotesColumn(serviceFees)) {
      tableHeaders.push({
        label: 'Notes',
        style: { width: '150px' },
      })
    }

    if (showExampleColumn) {
      tableHeaders.push({
        label: 'Example',
        style: { width: '150px' },
      })
    }

    if (showActionsColumn) {
      tableHeaders.push({
        label: 'Actions',
        className: 'sticky-column-end',
        style: { width: '100px' },
      })
    }

    return tableHeaders
  }

  getNotesFieldPlaceholder(fromCompanyName: string) {
    return `${fromCompanyName} retain the first 50% and then share 50% thereafter with the client`
  }

  getExampleFieldPlaceholder(fromCompanyName: string) {
    return `If you become a client of ${fromCompanyName}, we will receive fees of £250`
  }

  getServiceFeeGroupsForDisplay(options: {
    serviceFees: ServiceFeeDetail[]
    serviceAreas: ServiceAreaItem[]
  }): ServiceFeeRowGroups {
    const { serviceFees, serviceAreas } = options
    const groups: ServiceFeeRowGroups = {}

    serviceFees.forEach((serviceFee, serviceFeeIndex) => {
      const serviceAreaName = this.getServiceAreaName(serviceFee, serviceAreas)

      if (groups[serviceAreaName]) {
        groups[serviceAreaName].push({ ...serviceFee, index: serviceFeeIndex })
      } else {
        groups[serviceAreaName] = [{ ...serviceFee, index: serviceFeeIndex }]
      }
    })

    return groups
  }

  orderServiceFeesByName(
    serviceFees: ServiceFeeDetail[],
    serviceAreas: ServiceAreaItem[],
  ): ServiceFeeDetail[] {
    return serviceFees.slice().sort((a, b) => {
      const aServiceAreaName = this.getServiceAreaName(a, serviceAreas)
      const bServiceAreaName = this.getServiceAreaName(b, serviceAreas)

      return aServiceAreaName.localeCompare(bServiceAreaName)
    })
  }

  shouldShowExampleField(options: {
    agreementType?: CommercialAgreementTypeOption
    shareType: string
  }) {
    const { agreementType, shareType } = options

    return (
      agreementType === COMMERCIAL_AGREEMENT_TYPE.YFS &&
      shareType === SERVICE_FEE_SHARE_TYPE.percentage.value
    )
  }

  getExample(options: {
    serviceFee: Pick<
      ServiceFeeEntity,
      | 'defaultIntroductionExample'
      | 'defaultRetrospectiveExample'
      | 'groupIntroductionExample'
      | 'groupRetrospectiveExample'
    >
    consentLetterVariant: ReferralConsentLetterVariant
    referralConsentStage: ReferralConsentStage
  }): string | undefined | null {
    const { serviceFee, consentLetterVariant, referralConsentStage } = options

    switch (true) {
      case consentLetterVariant === REFERRAL_CONSENT_LETTER_VARIANT.DEFAULT &&
        referralConsentStage === REFERRAL_CONSENT_STAGE.INTRODUCTION:
        return serviceFee.defaultIntroductionExample
      case consentLetterVariant === REFERRAL_CONSENT_LETTER_VARIANT.DEFAULT &&
        referralConsentStage === REFERRAL_CONSENT_STAGE.RETROSPECTIVE:
        return serviceFee.defaultRetrospectiveExample
      case consentLetterVariant ===
        REFERRAL_CONSENT_LETTER_VARIANT.INTRA_GROUP_TRANSFER &&
        referralConsentStage === REFERRAL_CONSENT_STAGE.INTRODUCTION:
        return serviceFee.groupIntroductionExample
      case consentLetterVariant ===
        REFERRAL_CONSENT_LETTER_VARIANT.INTRA_GROUP_TRANSFER &&
        referralConsentStage === REFERRAL_CONSENT_STAGE.RETROSPECTIVE:
        return serviceFee.groupRetrospectiveExample
      default:
        return null
    }
  }
}

type ServiceFeeRowGroups = {
  [key: string]: Array<ServiceFeeDetail & { index: number }>
}

const serviceFeeService = new ServiceFeeService()

export default serviceFeeService
